import { catchError, EMPTY, Subject } from 'rxjs';
import { getRandomColor } from '../function';

/**
 * This class used to change and update
 * ui properties of loaded animation svg.
 */

export type BookAnimationOnChange = {
  type: string;
  target: string;
};

export type AnimationElementType = Element;
export type CacheElement =
  | SVGPathElement
  | NodeListOf<SVGElement | HTMLElement>
  | SVGElement
  | HTMLElement
  | Element
  | Node
  | undefined
  | null;

export class BookAnimationDom {
  private _resetQueue: Function[] = [];
  private _mainElement: AnimationElementType;
  private _svgCacheMap = new Map<string, CacheElement>();
  private _changeSubject$ = new Subject<BookAnimationOnChange>();

  constructor(private selector: string) {
    const element = document.querySelector(selector + ' svg');
    if (!element) {
      throw new Error('Element not founded');
    }

    this._mainElement = element;
  }

  public get change$() {
    return this._changeSubject$.pipe(catchError(() => EMPTY));
  }

  /**
   * The book svg element inside rendered svg
   * Other queries use this to get other elements
   */
  public get book(): CacheElement {
    const selector = '.book';

    if (this._svgCacheMap.has(selector)) {
      return this._svgCacheMap.get(selector);
    }

    return this._mainElement.querySelector(selector);
  }

  public get spring(): CacheElement {
    return this.getAndCache('.spring');
  }

  public get leftSheet(): CacheElement {
    return this.getAndCache('.left_sheet');
  }

  public get rightSheet(): CacheElement {
    return this.getAndCache('.right_sheet');
  }

  public get rightCoverPath(): CacheElement {
    return this.getAndCache(
      '.right_cover > g:nth-child(1) > path:nth-child(1)'
    );
  }

  public get leftCoverPath(): CacheElement {
    return this.getAndCache('.left_cover > g:nth-child(1) > path:nth-child(1)');
  }

  public get rightCover() {
    return this.getAndCache('.right_cover');
  }

  public get leftCover() {
    return this.getAndCache('.left_cover');
  }

  public get leftNotes(): CacheElement {
    return this.getAndCacheAll('.left_sheet .line');
  }

  public get rightNotes(): CacheElement {
    return this.getAndCacheAll('.right_sheet .line');
  }

  public get lines(): CacheElement {
    return this.getAndCacheAll('.line');
  }

  public get leftPicture(): CacheElement {
    return this.getAndCacheAll('.left_sheet .picture');
  }

  public get rightPicture(): CacheElement {
    return this.getAndCacheAll('.right_sheet .picture');
  }

  public get pictures(): CacheElement {
    return this.getAndCacheAll('.picture');
  }

  // ------------------------------------- Public methods|

  public toBlackWhitePrint() {
    this.restState();

    // Picture colors to gray scale 1
    (this.pictures as NodeListOf<HTMLElement>).forEach((element) => {
      element.style.filter = 'grayscale(100%)';

      this._takeReverseShot(() => {
        element.style.filter = 'grayscale(0)';
      });
    });
  }

  public toColorfulPrint() {
    this.restState();

    (this.lines as NodeListOf<HTMLElement>).forEach((element) => {
      element.style.opacity = '0';

      this._takeReverseShot(() => {
        element.style.opacity = '1';
      });
    });

    (this.leftPicture as NodeListOf<HTMLElement>).forEach((element) => {
      element.style.transform =
        'translateY(-10%) translateX(5%) scale(1.16, 1.4)';

      this._takeReverseShot(() => {
        element.style.transform = 'none';
      });
    });

    (this.rightPicture as NodeListOf<HTMLElement>).forEach((element) => {
      element.style.filter = 'none';
      element.style.transform =
        'translate(50px,-250px) scaleY(1.6) scaleX(1.2)';

      this._takeReverseShot(() => {
        element.style.transform = 'none';
      });
    });
  }

  public toHalfColorfulPrint() {
    this.restState();

    (this.pictures as NodeListOf<HTMLElement>).forEach((element) => {
      element.style.transform = 'none';
      element.style.opacity = '0';

      this._takeReverseShot(() => {
        element.style.opacity = '1';
      });
    });

    (this.lines as NodeListOf<HTMLElement>).forEach((line) => {
      line.style.opacity = '1';
      line.querySelector('path')!.style.stroke = getRandomColor();

      this._takeReverseShot(() => {
        line.querySelector('path')!.style.stroke = '#000';
      });
    });
  }

  public toMixedPrint() {
    this.restState();
    (this.rightSheet as SVGElement).style.filter = 'grayscale(100%)';

    this._takeReverseShot(() => {
      (this.rightSheet as SVGElement).style.filter = 'none';
    });
  }

  public toOneSidePrint() {
    (this.leftSheet as SVGElement)
      .querySelectorAll('.line, .picture')
      .forEach((element) => {
        element.querySelector('path')!.style.opacity = '0';
      });
  }

  public toTwoSidePrint() {
    (this.leftSheet as SVGElement)
      .querySelectorAll('.line, .picture')
      .forEach((element) => {
        element.querySelector('path')!.style.opacity = '1';
      });
  }

  public toTalcAndPabco() {
    // Change talc color to more transparent
    [this.leftCoverPath, this.rightCoverPath].forEach((cover: CacheElement) => {
      (cover as SVGElement).style.filter = 'blur(10px)';

      this._takeReverseShot(() => {
        (cover as SVGElement).style.filter = 'blur(0)';
      });
    });
  }

  public hideBinding() {
    [this.leftCover, this.rightCover].forEach((cover: CacheElement) => {
      (cover as SVGElement).style.opacity = '0';
    });

    (this.spring as SVGElement).style.opacity = '0';
  }

  public showBinding() {
    [this.leftCover, this.rightCover].forEach((cover: CacheElement) => {
      (cover as SVGElement).style.opacity = '1';
    });
    (this.spring as SVGElement).style.opacity = '1';
  }

  public changeTalcColor(color: string) {
    [this.leftCoverPath, this.rightCoverPath].forEach((cover: CacheElement) => {
      (cover as SVGElement).style.fill = color;
    });
  }

  // ------------------------------------- Private methods|

  private restState() {
    this._resetQueue.forEach((fn) => fn());
  }

  private getAndCache(selector: string): CacheElement {
    if (!this._svgCacheMap.has(selector)) {
      this._svgCacheMap.set(
        selector,
        (this.book as Element)?.querySelector(selector)
      );
    }

    return this._svgCacheMap.get(selector);
  }

  private getAndCacheAll(selector: string): CacheElement {
    if (!this._svgCacheMap.has(selector)) {
      this._svgCacheMap.set(
        selector,
        (this.book as HTMLElement)?.querySelectorAll(selector)
      );
    }
    return this._svgCacheMap.get(selector);
  }

  private _takeReverseShot(fn: Function): void {
    this._resetQueue.push(fn);
  }
}
